import { ArrowUpIcon, ResetIcon } from '@radix-ui/react-icons';
import { Box, Spinner } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { MachineIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { ActiveUsersCallout } from 'components/sections/admin-portal/machines/dialogs/active-users-callout';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { RestartMode } from 'lib_ts/enums/admin.enums';
import { FirmwareBranch } from 'lib_ts/enums/machine-msg.enum';
import {
  ServiceCategory,
  shouldCategoryResetTraining,
} from 'lib_ts/enums/machine-service-logs.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AdminMachinesService } from 'services/admin/machines.service';
import { MachineServiceLogsService } from 'services/machine-service-logs.service';
import { MachinesService } from 'services/machines.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'UpgradeFirmwareDialog';

interface IProps {
  machines: IAdminMachine[];
  onClose: (success: boolean) => void;
}

export const UpgradeFirmwareDialog = (props: IProps) => {
  const current = useAuthStore((s) => s.current);
  const loading = useMachinesPortalStore((s) => s.loading);

  const isSingle = props.machines.length === 1;

  const branchOptions = Object.values(FirmwareBranch).map((m) => {
    const o: IOption = {
      label: m.toUpperCase(),
      value: m,
    };

    return o;
  });

  const [branch, setBranch] = useState(FirmwareBranch.CURRENT);

  const hint = useMemo(() => {
    switch (branch) {
      case FirmwareBranch.CURRENT: {
        return 'Machine(s) will not checkout another branch before upgrading.';
      }

      default: {
        return `Machine(s) will checkout the \`${branch.toUpperCase()}\` branch before upgrading.`;
      }
    }
  }, [branch]);

  const machineIDs = useMemo(
    () => props.machines.map((m) => m.machineID),
    [props.machines]
  );

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        title="Upgrade Firmware"
        width={RADIX.DIALOG.WIDTH.MD}
        content={
          loading ? (
            <Spinner />
          ) : (
            <CommonFormGrid columns={1}>
              <Box>
                <ReactMarkdown
                  children={[
                    'Each machine will check for any firmware updates and apply them if found.',
                    'Please ensure each machine is not in use before proceeding.',
                    'Each machine will need to be rebooted/restarted after the updates complete.',
                    'Any active or waiting users will be disconnected.',
                    `The following ${
                      isSingle ? 'machine' : 'machines'
                    } will be upgraded:`,
                    machineIDs.map((machineID) => ` - ${machineID}`).join('\n'),
                  ].join('\n\n')}
                />
              </Box>
              <ActiveUsersCallout machineIDs={machineIDs} />
              <CommonSelectInput
                id="firmware-branch"
                name="branch"
                label="Branch"
                options={branchOptions}
                value={branch}
                onChange={(v) => setBranch(v as FirmwareBranch)}
                hint_md={hint}
              />
            </CommonFormGrid>
          )
        }
        buttons={[
          {
            icon: <ResetIcon />,
            label: 'Restart OS',
            color: RADIX.COLOR.WARNING,
            tooltip:
              'This will restart the operating system on the machine(s).',
            onClick: () => {
              MachinesService.getInstance().restart(RestartMode.os, machineIDs);
            },
            loading,
          },
          {
            icon: <MachineIcon />,
            label: 'Restart FW',
            color: RADIX.COLOR.DANGER,
            tooltip: 'This will restart the firmware on the machine(s).',
            onClick: () => {
              MachinesService.getInstance().restart(
                RestartMode.arc,
                machineIDs
              );
            },
            loading,
          },
          {
            label: 'Upgrade',
            color: RADIX.COLOR.SUCCESS,
            variant: 'solid',
            icon: <ArrowUpIcon />,
            onClick: async () => {
              // log that an attempt is being made to service logs
              const now = new Date().toISOString();
              const shouldReset = shouldCategoryResetTraining(
                ServiceCategory.FirmwareUpgrade
              );

              for (const machine of props.machines) {
                MachineServiceLogsService.getInstance().createOne({
                  machineID: machine.machineID,
                  serviced: now,
                  servicedBy: current.email,
                  category: ServiceCategory.FirmwareUpgrade,
                  notes: `Previous version: ${machine.last_firmware} (${machine.last_firmware_updated})`,
                  trainingReset: shouldReset,
                });
              }

              AdminMachinesService.getInstance()
                .upgradeFirmware({
                  machineIDs: machineIDs,
                  branch: branch,
                })
                .then(() => {
                  NotifyHelper.info({
                    message_md: `Starting firmware upgrade for ${machineIDs.join(
                      ', '
                    )}...`,
                  });
                })
                .catch((error) => {
                  console.error(error);
                  NotifyHelper.info({
                    message_md: `Failed to perform firmware upgrade for ${machineIDs.join(
                      ', '
                    )}, please check console.`,
                  });
                });
            },
            loading,
          },
        ]}
        onClose={() => props.onClose(false)}
      />
    </ErrorBoundary>
  );
};
